import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

const HunterDropdown = ({ onHunterSelect, selectedHunterId, currentHunterName }) => {
    const [hunters, setHunters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const fetchHunters = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`${import.meta.env.VITE_BASE_URL}/hunters_list`);
                if (!response.ok) throw new Error('Failed to fetch hunters');
                const data = await response.json();
                
                if (Array.isArray(data)) {
                    setHunters(data.map(hunter => ({
                        id: hunter.id || 0,
                        name: hunter.username || 'Unknown Hunter'
                    })));
                } else {
                    throw new Error('Invalid data format');
                }
            } catch (error) {
                console.error('Error:', error);
                setError(error.message);
                setHunters([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHunters();
    }, []);

    const filteredHunters = hunters.filter(hunter => {
        const name = hunter?.name?.toString().toLowerCase() || '';
        const id = hunter?.id?.toString() || '';
        const term = searchTerm.toLowerCase();
        return name.includes(term) || id.includes(term);
    });

    const handleSelect = (hunterId, hunterName) => {
        onHunterSelect(hunterId, hunterName);
        setIsOpen(false);
        setSearchTerm('');
    };

    const selectedHunter = hunters.find(h => h.id === selectedHunterId) || 
                         { id: selectedHunterId, name: currentHunterName };

    return (
        <div className="items-start w-1/4">
            {/* Display box showing current selection */}
            <div 
                className="flex items-center justify-between border border-gray-300 rounded-md p-2 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="truncate">
                    {selectedHunter?.name || 'Select a hunter...'}
                </span>
                <svg 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </div>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-1/4 bg-white border border-gray-300 rounded-md">
                    {/* Search input */}
                    <div className="p-2 border-b">
                        <input
                            type="text"
                            placeholder="Search hunters..."
                            className="w-full p-2 border rounded-md outline-none focus:ring-2 focus:ring-blue-200"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Loading state */}
                    {isLoading && (
                        <div className="p-4 text-center text-gray-500">
                            Loading hunters...
                        </div>
                    )}

                    {/* Error state */}
                    {error && (
                        <div className="p-4 text-center text-red-500">
                            {error}
                        </div>
                    )}

                    {/* Hunters list */}
                    {!isLoading && !error && (
                        <div className="max-h-60 overflow-y-auto">
                            {filteredHunters.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    {searchTerm ? 'No matching hunters found' : 'No hunters available'}
                                </div>
                            ) : (
                                <ul>
                                    {filteredHunters.map((hunter) => (
                                        <li 
                                            key={hunter.id}
                                            className={`p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${
                                                hunter.id === selectedHunterId ? 'bg-blue-50' : ''
                                            }`}
                                            onClick={() => handleSelect(hunter.id, hunter.name)}
                                        >
                                            <span>{hunter.name}</span>
                                            <span className="text-sm text-gray-500">#{hunter.id}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const OpportunityInfoTab = ({ oppData }) => {
    const [leadJourney, setLeadJourney] = useState({});
    const [selectedHunter, setSelectedHunter] = useState({
        id: null,
        name: null
    });
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
    const lead_id = oppData?.referred_lead_id;
    const [userId, setUserId] = useState(null);

    useEffect(() => {
        if (!router.isReady || !oppData?.lead_id) return;
        const storedUserId = Number(localStorage.getItem("userId"));
        setUserId(storedUserId);

        const fetchData = async () => {
            try {
                // Fetch lead journey
                const journeyUrl = oppData.funnel === 'Referral' 
                    ? `${import.meta.env.VITE_BASE_URL}/opp_referral_details?lead_id=${oppData.referred_lead_id}`
                    : `${import.meta.env.VITE_BASE_URL}/lead_journey_tbl?lead_id=${oppData.lead_id}`;
                
                const journeyResponse = await fetch(journeyUrl);
                const journeyData = await journeyResponse.json();
                
                setLeadJourney(journeyData);
                setSelectedHunter({
                    id: journeyData.hunter_id,
                    name: journeyData.hunter_name
                });
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load lead data');
            }
        };

        fetchData();
    }, [router.isReady, oppData?.lead_id]);

    const handleSubmit = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_BASE_URL}/update_lead_hunter`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    lead_id: lead_id,
                    hunter_id: selectedHunter.id,
                    user_id: userId
                }),
            });
            
            const data = await response.json();
            toast.success('Hunter successfully reassigned!');
            setLeadJourney(prev => ({
                ...prev,
                hunter_id: selectedHunter.id,
                hunter_name: selectedHunter.name
            }));
        } catch (error) {
            toast.error('Failed to reassign hunter');
            console.error('Error:', error);
        } finally {
            setShowConfirmationModal(false);
        }
    };

    return (
        <div className='w-full bg-white p-7 rounded-3xl'>
            <h1 className="text-xl font-bold text-gray-800 mb-6">Assign to Hunter</h1>
            
            <div className="flex items-start gap-4">
                <HunterDropdown 
                    onHunterSelect={(id, name) => setSelectedHunter({ id, name })}
                    apiUrl={`${import.meta.env.VITE_BASE_URL}/hunters_list`}
                    selectedHunterId={selectedHunter.id}
                    currentHunterName={leadJourney.hunter_name}
                />
                
                {selectedHunter.id !== leadJourney.hunter_id && (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSelectedHunter({
                                id: leadJourney.hunter_id,
                                name: leadJourney.hunter_name
                            })}
                            className="px-4 py-2 border border-gray-300 rounded-3xl hover:bg-gray-100 transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => setShowConfirmationModal(true)}
                            className="px-4 py-2 rounded-3xl hover:bg-yellow-400 bg-yellow-300 text-black transition"
                        >
                            Confirm Reassignment
                        </button>
                    </div>
                )}
            </div>

            {showConfirmationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center text-center">
                                    
                <div className="bg-white p-6 rounded-lg w-1/4">
                    {/* Center the icon using Flexbox */}
                    <div className="flex justify-center mb-2 pt-6">
                        <img src="/icons/edit.svg" className='size-11' />
                    </div>

                    <h1 className="text-2xl font-bold mb-3">Save & Update Info Gathering</h1>
                    <p>Info Gathering details will be updated. Please ensure new details are correct.</p>
                    <div className="mt-6 mb-6">
                        <button
                            className="text-black underline hover:text-gray-600 focus:outline-none"
                            onClick={() => setShowConfirmationModal(false)}
                        >
                            <b>No, Don't Update</b>
                        </button>
                        
                    </div>
                    <button
                            className="bg-yellow-300 text-black py-4 w-full rounded-3xl hover:bg-yellow-400"
                            onClick={handleSubmit}
                        >
                            <b>Save & Update</b>
                        </button>
                </div>
                </div>
            )}
        </div>
    );
};

export default OpportunityInfoTab;